<template>
  <!-- 分店綁定服務人員的資訊；沒有綁定時只顯示說明 -->
  <section v-loading="loading" class="staff-overview">
    <p v-if="overview && !isBound" class="unbound-hint">{{ $t('homePage.staffOverview.unbound') }}</p>

    <template v-if="isBound">
      <div class="stat-cards">
        <div v-for="card in statCards" :key="card.key" class="card stat-card">
          <div class="stat-icon">
            <MaterialIcon :size="20" color="var(--primary-100)">{{ card.icon }}</MaterialIcon>
          </div>
          <div class="flex flex-col" style="gap: 4px">
            <p class="stat-label">{{ card.label }}</p>
            <p class="stat-value">{{ card.value }}</p>
            <p v-if="card.sub" class="stat-sub" :class="card.subClass">{{ card.sub }}</p>
          </div>
        </div>
      </div>

      <div class="main-row">
        <div class="card reservation-card">
          <div class="card-header">
            <div>
              <p class="card-title">{{ $t('homePage.staffOverview.reservationList') }}</p>
              <p class="card-sub">
                {{ $t('homePage.staffOverview.reservationListSub', { date: todayText, count: overview.todayReservationCount }) }}
              </p>
            </div>
            <BaseElButton
              v-if="checkAction('adminView.appointmentCalendarByDay.page')"
              type="primary"
              plain
              @click="$router.push({ name: 'DailyView' })"
            >
              {{ $t('homePage.staffOverview.viewCalendar') }}
            </BaseElButton>
          </div>

          <EmptyBlock v-if="!reservations.length" :emptyText="$t('homePage.staffOverview.noReservation')" />
          <div v-else class="reservation-table">
            <div class="reservation-row table-head">
              <p>{{ $t('homePage.staffOverview.table.time') }}</p>
              <p>{{ $t('homePage.staffOverview.table.customer') }}</p>
              <p>{{ $t('homePage.staffOverview.table.service') }}</p>
              <p>{{ $t('homePage.staffOverview.table.status') }}</p>
            </div>
            <div v-for="row in reservations" :key="row.id" class="reservation-row">
              <div>
                <p class="time">{{ row.time }}</p>
                <p class="text-sub text-gray-60">{{ $t('homePage.staffOverview.minutes', { minutes: row.minutes }) }}</p>
              </div>
              <div class="flex items-center" style="gap: 8px">
                <span class="avatar">{{ row.initial }}</span>
                <p>{{ row.userName || '-' }}</p>
              </div>
              <p>{{ row.serviceName || '-' }}</p>
              <div>
                <Tag v-if="row.status" :type="row.status.tag" size="small">{{ $t(row.status.name) }}</Tag>
              </div>
            </div>
          </div>
        </div>

        <div class="card strengths-card">
          <div class="card-header">
            <div class="flex items-center" style="gap: 8px">
              <MaterialIcon :size="20" color="var(--primary-100)">auto_awesome</MaterialIcon>
              <p class="card-title">{{ $t('homePage.staffOverview.strengths') }}</p>
            </div>
            <router-link v-if="performanceRoute" :to="performanceRoute" class="link">
              {{ $t('homePage.staffOverview.viewPerformance') }}
            </router-link>
          </div>
          <p v-if="advice && !advice.isBenchmarkLatest" class="card-sub">
            {{ $t('homePage.staffOverview.dataPeriod', { range: benchmarkRange }) }}
          </p>

          <p v-if="!advice" class="empty-text">{{ $t('homePage.staffOverview.noAdvice') }}</p>
          <p v-else-if="!advice.strengths.length" class="empty-text">{{ $t('homePage.staffOverview.noStrength') }}</p>
          <div v-else class="flex flex-col" style="gap: 12px">
            <AdviceMetricItem v-for="item in advice.strengths" :key="item.metricKey" :item="item" />
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { computed, defineComponent, onMounted } from 'vue'
import { get } from 'lodash'
import { i18n } from '@/plugins/i18n/i18n'
import MaterialIcon from '@/components/MaterialIcon.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import Tag from '@/components/Tag/Tag.vue'
import AdviceMetricItem from '@/views/BeautyAIAppointmentUnitAnalysisDetail/components/AdviceMetricItem.vue'
import { orderStatusConfig } from '@/config/reservation'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'
import {
  useShopHomePageStaffOverview,
  formatAmountChange,
  formatReservationStart,
} from '@/use/useShopHomePageStaffOverview'
import { localeString } from '@/utils/helper'
import dayjs from '@/lib/dayjs'

// YYYY-MM → YYYY/MM
const formatMonth = (month) => (month ? month.replace('-', '/') : '')

export default defineComponent({
  name: 'StaffOverview',
  components: { MaterialIcon, EmptyBlock, Tag, AdviceMetricItem },
  setup () {
    const { overview, loading, isBound, getOverview } = useShopHomePageStaffOverview()
    const { shopTimezone } = useShop()
    const { checkAction } = usePermissions()
    const t = (key, values) => i18n.t(`homePage.staffOverview.${key}`, values)

    const todayText = computed(() => dayjs(new Date(), shopTimezone.value).format('MM/DD'))

    const statCards = computed(() => {
      const { todayReservationCount, todayReservations, nextReservation, monthAmount, lastMonthSamePeriodAmount } = overview.value
      const completedCount = todayReservations.filter(({ status }) => status === 'complete').length
      const change = formatAmountChange(monthAmount, lastMonthSamePeriodAmount)
      return [
        {
          key: 'today',
          icon: 'event',
          label: t('todayCount'),
          value: todayReservationCount,
          sub: t('completedCount', { count: completedCount }),
        },
        {
          key: 'next',
          icon: 'schedule',
          label: t('nextCustomer'),
          value: nextReservation ? formatReservationStart(nextReservation.start, shopTimezone.value) : '-',
          sub: nextReservation
            ? [nextReservation.userName, nextReservation.serviceName].filter(Boolean).join('・')
            : t('noNextCustomer'),
        },
        {
          key: 'amount',
          icon: 'trending_up',
          label: t('monthAmount'),
          value: `NT$ ${localeString(monthAmount) || 0}`,
          sub: change ? t('vsLastMonth', { change }) : t('noLastMonthAmount'),
          subClass: change && (change.startsWith('-') ? 'is-down' : 'is-up'),
        },
      ]
    })

    const reservations = computed(() => get(overview.value, 'todayReservations', []).map((row) => ({
      ...row,
      time: dayjs(row.start, shopTimezone.value).format('HH:mm'),
      minutes: dayjs(row.end).diff(dayjs(row.start), 'minute'),
      initial: (row.userName || '').trim().charAt(0),
      status: orderStatusConfig[row.status],
    })))

    const advice = computed(() => get(overview.value, 'advice'))
    const benchmarkRange = computed(() => `${formatMonth(get(advice.value, 'benchmarkFromMonth'))}～${formatMonth(get(advice.value, 'benchmarkPeriod'))}`)
    const performanceRoute = computed(() => {
      if (!advice.value || !checkAction('admin.beautyAIAptUnitAnalysis.page')) return null
      return { name: 'BeautyAIAppointmentUnitAnalysisDetail', params: { id: overview.value.appointmentUnit.id } }
    })

    onMounted(getOverview)

    return {
      overview,
      loading,
      isBound,
      checkAction,
      todayText,
      statCards,
      reservations,
      advice,
      benchmarkRange,
      performanceRoute,
    }
  },
})
</script>

<style lang="postcss" scoped>
.staff-overview {
  @apply flex flex-col gap-[16px];
}
.unbound-hint {
  @apply text-sub text-gray-60;
}
.card {
  @apply bg-white rounded-[8px] p-[20px];
}
.stat-cards {
  @apply grid grid-cols-3 gap-[16px];
}
.stat-card {
  @apply flex items-start gap-[12px];
}
.stat-icon {
  @apply flex items-center justify-center flex-shrink-0 w-[36px] h-[36px] rounded-[8px] bg-primary-30;
}
.stat-label {
  @apply text-sub text-gray-60;
}
.stat-value {
  @apply text-[24px] leading-[32px] font-bold text-gray-100;
}
.stat-sub {
  @apply text-sub text-gray-60;
}
.stat-sub.is-up {
  @apply text-success;
}
.stat-sub.is-down {
  @apply text-danger;
}
.main-row {
  @apply grid grid-cols-3 gap-[16px] items-start;
}
.reservation-card {
  @apply col-span-2 flex flex-col gap-[16px];
}
.strengths-card {
  @apply flex flex-col gap-[12px];
}
.card-header {
  @apply flex items-center justify-between gap-[8px];
}
.card-title {
  @apply font-medium text-gray-100;
}
.card-sub {
  @apply text-sub text-gray-60;
}
.reservation-table {
  @apply flex flex-col;
}
.reservation-row {
  @apply grid items-center gap-[12px] py-[12px] border-b border-gray-40 text-gray-100;
  grid-template-columns: 1fr 1.5fr 2fr 1fr;
}
.reservation-row:last-child {
  @apply border-b-0;
}
.table-head {
  @apply py-[8px] text-sub text-gray-60;
}
.time {
  @apply font-bold;
}
.avatar {
  @apply flex items-center justify-center flex-shrink-0 w-[28px] h-[28px] rounded-full bg-primary-30 text-primary-100 text-sub;
}
.link {
  @apply text-sub text-primary-100 flex-shrink-0;
}
.empty-text {
  @apply text-sub text-gray-60;
}
@media screen and (max-width: 1200px) {
  .main-row {
    @apply grid-cols-1;
  }
  .reservation-card {
    @apply col-span-1;
  }
}
@media screen and (max-width: 768px) {
  .stat-cards {
    @apply grid-cols-1;
  }
}
</style>
